import React, { useState, useEffect } from 'react';
import { Button } from "@shared/ui/button";
import { Check, User, Mail, Hash, Home, MapPin, Settings, LogIn, LayoutDashboard, Receipt, Droplet, MessageSquare, BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

export const Done = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(20);

  const consumerNo = sessionStorage.getItem('create_account_consumer_no') || '';
  const firstName = sessionStorage.getItem('create_account_first_name') || '';
  const lastName = sessionStorage.getItem('create_account_last_name') || '';
  const accountType = sessionStorage.getItem('create_account_type') || '';
  const email = sessionStorage.getItem('create_account_email') || '';
  const address = sessionStorage.getItem('create_account_address') || '';
  
  let services: string[] = [];
  try {
    services = JSON.parse(sessionStorage.getItem('create_account_services') || '[]');
  } catch(e) {}

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      navigate('/login');
    }
  }, [countdown, navigate]);

  return (
    <div className="flex flex-col p-2 items-center text-center">
      <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-4 mt-2">
        <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center text-white shadow-sm ring-4 ring-green-100">
          <Check className="h-6 w-6" strokeWidth={3} />
        </div>
      </div>
      
      <div className="bg-green-100 text-green-800 text-[10px] font-bold px-3 py-1 rounded-full mb-4 flex items-center">
        🎉 {t("Account Created Successfully")}
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-3">{t("Welcome to SMART360!")}</h2>
      <p className="text-gray-500 text-sm mb-6 max-w-sm">
        {t("Your utility account is ready. You can now pay bills, track usage, and manage all your utility services online.")}
      </p>
      
      <div className="bg-white border border-gray-200 rounded-xl p-5 w-full text-left mb-6 relative">
        <h3 className="text-[10px] font-bold text-gray-400  tracking-wider mb-4 text-center">{t("Your Account Summary")}</h3>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <User className="h-4 w-4 mr-3 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Name")}</div>
              <div className="font-medium text-gray-900 text-sm">{firstName} {lastName}</div>
            </div>
          </div>
          <div className="flex items-start">
            <Mail className="h-4 w-4 mr-3 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Email")}</div>
              <div className="font-medium text-gray-900 text-sm">{email}</div>
            </div>
          </div>
          <div className="flex items-start">
            <Hash className="h-4 w-4 mr-3 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Consumer No.")}</div>
              <div className="font-medium text-gray-900 text-sm">{consumerNo}</div>
            </div>
          </div>
          <div className="flex items-start">
            <Home className="h-4 w-4 mr-3 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Account Type")}</div>
              <div className="font-medium text-gray-900 text-sm">{accountType}</div>
            </div>
          </div>
          <div className="flex items-start">
            <MapPin className="h-4 w-4 mr-3 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Service Address")}</div>
              <div className="font-medium text-gray-900 text-sm">{address}</div>
            </div>
          </div>
          <div className="flex items-start">
            <Settings className="h-4 w-4 mr-3 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Services")}</div>
              <div className="flex gap-2 mt-1 flex-wrap">
                {services.map((service, idx) => (
                  <span key={idx} className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-medium border border-blue-200">
                    {service}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-sm text-gray-500 mb-4 flex items-center justify-center gap-2">
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        {t("Redirecting to login in ")} <span className="font-bold text-blue-600">{countdown}s</span>
      </div>
      
      <div className="flex gap-3 w-full mb-8">
        <Button onClick={() => navigate('/login')} className="flex-1 bg-blue-600 hover:bg-blue-700 h-12 text-base">
           <LogIn className="h-4 w-4 mr-2" /> {t("Sign In Now")}
        </Button>
        <Button variant="outline" onClick={() => navigate('/login')} className="flex-1 h-12 text-base bg-white">
           <LayoutDashboard className="h-4 w-4 mr-2 text-gray-500" /> {t("View Portal")}
        </Button>
      </div>

      {/* Info Card - Outside the flow visually */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 w-full text-left shadow-sm">
        <h4 className="font-bold text-gray-900 text-sm mb-4">{t("What you can do now")}</h4>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="bg-blue-50 p-1.5 rounded-lg mr-3 shrink-0">
              <Receipt className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{t("Pay your utility bill online — no more standing in line")}</div>
          </div>
          <div className="flex items-start">
            <div className="bg-blue-50 p-1.5 rounded-lg mr-3 shrink-0">
              <Droplet className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{t("Track your monthly water consumption and spot leaks early")}</div>
          </div>
          <div className="flex items-start">
            <div className="bg-blue-50 p-1.5 rounded-lg mr-3 shrink-0">
              <MessageSquare className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{t("Raise complaints and service requests and track them live")}</div>
          </div>
          <div className="flex items-start">
            <div className="bg-blue-50 p-1.5 rounded-lg mr-3 shrink-0">
              <BellRing className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{t("Receive instant alerts for outages and maintenance")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
